import { stringify } from 'query-string';

import { HEART_DEFAULT_LIST_ID, HEART_DEFAULT_LIST_TYPE, MAFIA_EMAIL_TOKEN } from 'constants/apis';
import { MERCHANTID_MARKET_NAME_MAPPING } from 'helpers/MarketplaceUtils';
import { getSlotzAuthConfig } from 'helpers/MyAccountUtils';
import timedFetch from 'middleware/timedFetch';
import { fetchOpts } from 'apis/mafia/common';

const SLOTZ_MY_ACCOUNT_ENDPOINT = '/mobileapi/accountapi/a';
const SLOTZ_MY_ACCOUNT_CUSTOMER_INFO_ENDPOINT = '/mobileapi/accountapi/customerInfo';
const SLOTZ_MY_ACCOUNT_VERSION = 'v1';

export function getBirthday({ url }, fetcher = timedFetch('getBirthday')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_CUSTOMER_INFO_ENDPOINT}/getBirthday`, getSlotzAuthConfig());
}

export function updateBirthday({ url }, birthday, fetcher = timedFetch('updateBirthday')) {
  const authConfig = getSlotzAuthConfig();

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_CUSTOMER_INFO_ENDPOINT}/updateBirthday`, {
    ...authConfig,
    body: JSON.stringify({ birthday }),
    headers: {
      ...authConfig.headers,
      'Content-Type': 'application/json'
    },
    method: 'POST'
  });
}

// Get authenticated tracking information for order shipment
export const getAuthTracking = ({ url }, orderId, shipmentId, credentials, fetcher = timedFetch('getAuthTracking')) => {
  const searchParams = new URLSearchParams();

  // Required tracking parameters
  searchParams.append('orderId', orderId);
  searchParams.append('shipmentId', shipmentId);

  // Required tracking parameter
  searchParams.append('includeTracking', 'true');

  // Build mobile API endpoint URL
  const fullUrl = `${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/orders/getTracking?${searchParams.toString()}`;

  return fetcher(fullUrl, fetchOpts({}, credentials));
};

export function getGuestTracking({ url }, orderId, shipmentId, credentials, authToken, fetcher = timedFetch('getGuestTracking')) {
  return fetcher(
    `${url}/mobileapi/accountapi/e/orders/getTracking?orderId=${orderId}&shipmentId=${shipmentId}`,
    fetchOpts(
      {
        headers: {
          [MAFIA_EMAIL_TOKEN]: authToken
        }
      },
      credentials
    )
  );
}

/*
  For /accountapi/cloudlist endpoints see: https://confluence.zappos.net/display/~jsenecal/Cloud+List+API
  Worth noting these cloudlist calls are shared on Search as well.
*/
export function getHeartList({ url }, { shareToken, listId = 'h.', nextPageToken }, fetcher = timedFetch('getHeartList')) {
  let query = shareToken ? { shareToken } : { listId };
  query = nextPageToken ? stringify({ ...query, nextPageToken }) : stringify(query);

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/listItems?${query}`, getSlotzAuthConfig());
}

export function getListOfIds({ url }, listId = HEART_DEFAULT_LIST_ID, fetcher = timedFetch('getHeartIdList')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/itemIdsOnList?listId=${listId}`, getSlotzAuthConfig());
}

export function addToList({ url }, { itemId, subItemId, merchantId, listId = HEART_DEFAULT_LIST_ID }, fetcher = timedFetch('addToHeartList')) {
  const merchantIdToMarketName = MERCHANTID_MARKET_NAME_MAPPING[merchantId];
  const marketParam = (merchantIdToMarketName && `?market=${merchantIdToMarketName}`) || '';

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/addToList${marketParam}`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      itemId,
      subItemId,
      listId
    })
  });
}

export function removeFromList(
  { url },
  { itemId, subItemId, merchantId, listId = HEART_DEFAULT_LIST_ID },
  fetcher = timedFetch('removeFromHeartList')
) {
  const merchantIdToMarketName = MERCHANTID_MARKET_NAME_MAPPING[merchantId];
  const marketParam = (merchantIdToMarketName && `?market=${merchantIdToMarketName}`) || '';

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/removeFromList${marketParam}`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      itemId,
      subItemId,
      listId
    })
  });
}

export function createList({ url }, { listTypeId = HEART_DEFAULT_LIST_TYPE, listName }, fetcher = timedFetch('createHeartList')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/createList`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      type: listTypeId,
      name: listName
    })
  });
}

export function shareList({ url }, listId, fetcher = timedFetch('createShareList')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/shareList`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      listId
    })
  });
}

export function deleteList({ url }, listId, fetcher = timedFetch('deleteHeartLists')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/deleteList`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      listId
    })
  });
}

export function uploadImage({ url }, { listId, imageLocation, headerImageFile }, fetcher = timedFetch('uploadImage')) {
  const formData = new FormData();
  formData.append('file', headerImageFile);
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/v2/uploadImage?listId=${listId}&imageLocation=${imageLocation}`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: formData
  });
}

export function deleteImage({ url }, { listId, type }, fetcher = timedFetch('deleteImage')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/v2/deleteImage`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      listId,
      type
    })
  });
}

export function makePublic({ url }, listId, fetcher = timedFetch('makePublic')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/v2/makePublic`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      listId
    })
  });
}

export function makePrivate({ url }, listId, fetcher = timedFetch('makePrivate')) {
  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/v2/makePrivate`, {
    ...getSlotzAuthConfig(),
    method: 'POST',
    body: JSON.stringify({
      listId
    })
  });
}

export function recordMyAccountError(qs, fetcher = timedFetch('postMartyPixel')) {
  const reqUrl = `/martypixel?${qs}`;
  return fetcher(reqUrl, fetchOpts({ method: 'post' }));
}

export function getListInfo({ url }, { listId = 'h.', shareToken }, fetcher = timedFetch('getListInfo')) {
  const query = shareToken ? stringify({ shareToken }) : stringify({ listId });

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/list?${query}`, getSlotzAuthConfig());
}

export function getItemSubsetOnLists({ url }, { itemId }, fetcher = timedFetch('getItemSubsetOnLists')) {
  return fetcher(
    `${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/itemSubsetOnLists?itemIds=${itemId}&type=h`,
    getSlotzAuthConfig()
  );
}

export function getAllLists(
  { url },
  { listId = HEART_DEFAULT_LIST_TYPE, shareToken = null, nextPageToken = null },
  fetcher = timedFetch('getAllLists')
) {
  // use a share token, or listId
  let query = shareToken ? { shareToken } : { type: listId };
  query = nextPageToken ? stringify({ ...query, nextPageToken }) : stringify(query);

  return fetcher(`${url}${SLOTZ_MY_ACCOUNT_ENDPOINT}/cloudlist/${SLOTZ_MY_ACCOUNT_VERSION}/lists?${query}`, getSlotzAuthConfig());
}
